import React, { useState, useEffect } from 'react';
import { Save, RotateCcw, Lock, Users, AlertTriangle } from 'lucide-react';
import { dashboardApi } from '../services/api';

/**
 * Runtime settings, grouped by section.
 *
 * Every value shows where it came from — database, env, or coded default — so
 * it is always clear whether the panel is actually in control of a setting.
 * Targeting changes show a live "matches N influencers" count before saving,
 * so nobody discovers a filter selects zero people after saving it.
 */

const SECTION_LABEL = {
  targeting: 'Targeting',
  sending: 'Sending & ramp',
  ai: 'AI & replies',
  campaign: 'Campaign',
  safety: 'Safety',
  scraper: 'Scraper',
  system: 'System',
  general: 'Other'
};

const SOURCE_CLASS = {
  database: 'bg-green-100 text-green-700',
  env: 'bg-yellow-100 text-yellow-700',
  default: 'bg-gray-100 text-gray-600'
};

const AdminSettings = () => {
  const [categories, setCategories] = useState({});
  const [drafts, setDrafts] = useState({});
  const [matches, setMatches] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = async () => {
    try {
      const { data } = await dashboardApi.getAdminSettings();
      setCategories(data.categories || {});
      setDrafts({});
      setError(null);
    } catch (err) { setError(err.response?.data?.error || err.message); }
  };
  useEffect(() => { load(); }, []);

  const valueOf = (item) =>
    drafts[item.key] !== undefined ? drafts[item.key]
      : (Array.isArray(item.value) ? item.value.join(',') : String(item.value ?? ''));

  const save = async (item) => {
    setBusy(item.key);
    try {
      const raw = drafts[item.key];
      await dashboardApi.updateAdminSetting(item.key,
        item.type === 'boolean' ? raw === true || raw === 'true' : raw);
      setNotice(`${item.label} saved — live on the next cycle`);
      await load();
      setTimeout(() => setNotice(null), 4000);
    } catch (err) { setError(err.response?.data?.error || err.message); }
    finally { setBusy(null); }
  };

  const reset = async (item) => {
    setBusy(item.key);
    try { await dashboardApi.resetAdminSetting(item.key); await load(); }
    catch (err) { setError(err.response?.data?.error || err.message); }
    finally { setBusy(null); }
  };

  /** Live match count for the targeting section, using the draft values. */
  const previewTargeting = async () => {
    const targeting = categories.targeting || [];
    const get = (key) => {
      const item = targeting.find(i => i.key === key);
      return item ? valueOf(item) : undefined;
    };
    setBusy('preview');
    try {
      const { data } = await dashboardApi.previewTargeting({
        target_country: get('TARGET_COUNTRY'),
        target_min_followers: Number(get('TARGET_MIN_FOLLOWERS')) || undefined,
        target_max_followers: Number(get('TARGET_MAX_FOLLOWERS')) || undefined,
        target_niches: get('TARGET_NICHES')
      });
      setMatches(data.matches);
    } catch (err) { setError(err.response?.data?.error || err.message); }
    finally { setBusy(null); }
  };

  const renderItem = (item) => (
    <div key={item.key} className="py-3 border-b border-gray-100 last:border-0">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-800">{item.label}</label>
            <span className={`px-1.5 text-xs rounded ${SOURCE_CLASS[item.source]}`}>{item.source}</span>
            {!item.editable && <Lock className="w-3.5 h-3.5 text-gray-400" />}
          </div>
          {item.help && <p className="text-xs text-gray-500 mt-0.5">{item.help}</p>}
          <p className="text-xs text-gray-400 font-mono mt-0.5">{item.key}</p>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {item.type === 'boolean' ? (
            <button
              disabled={!item.editable || busy === item.key}
              onClick={() => setDrafts({ ...drafts, [item.key]: !(valueOf(item) === 'true') })}
              className={`px-3 py-1.5 text-sm rounded-lg border disabled:opacity-50 ${
                valueOf(item) === 'true'
                  ? 'bg-green-600 text-white border-green-600'
                  : 'bg-white text-gray-700 border-gray-300'}`}>
              {valueOf(item) === 'true' ? 'ON' : 'OFF'}
            </button>
          ) : (
            <input
              disabled={!item.editable}
              className="w-56 border border-gray-300 rounded-lg px-2 py-1.5 text-sm disabled:bg-gray-50 disabled:text-gray-400"
              value={valueOf(item)}
              onChange={(e) => setDrafts({ ...drafts, [item.key]: e.target.value })} />
          )}
          <button onClick={() => save(item)}
            disabled={!item.editable || busy === item.key || drafts[item.key] === undefined}
            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-30" title="Save">
            <Save className="w-4 h-4" />
          </button>
          <button onClick={() => reset(item)}
            disabled={!item.editable || item.source !== 'database'}
            className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg disabled:opacity-30"
            title="Reset to env/default">
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  const order = ['targeting','sending','ai','campaign','safety','scraper','system','general'];

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Settings</h1>
      <p className="text-sm text-gray-500 mb-6">
        Changes take effect on the next cycle — no restart. Secrets stay in <code>.env</code> and are never shown here.
      </p>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
      {notice && <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">{notice}</div>}

      {order.filter(c => categories[c]).map((cat) => (
        <div key={cat} className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">{SECTION_LABEL[cat] || cat}</h2>
            {cat === 'targeting' && (
              <div className="flex items-center gap-3">
                {matches !== null && (
                  <span className="text-sm text-gray-700 flex items-center">
                    <Users className="w-4 h-4 mr-1.5 text-gray-400" />
                    <strong>{matches.toLocaleString()}</strong>&nbsp;influencers match
                  </span>
                )}
                <button onClick={previewTargeting} disabled={busy === 'preview'}
                  className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                  Preview match count
                </button>
              </div>
            )}
          </div>

          {cat === 'safety' && (
            <div className="mx-6 mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800 flex items-start">
              <AlertTriangle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
              Shown for visibility, deliberately not editable here. Turning test mode off is what
              lets real influencers receive email, so it stays a server-side change.
            </div>
          )}

          <div className="px-6 py-2">{categories[cat].map(renderItem)}</div>
        </div>
      ))}
    </div>
  );
};

export default AdminSettings;
